import React from "react";

import EntityIcon from "../../RMS/Dashboard/Components/EntityIcon";
import type { EntityKind } from "../../RMS/Dashboard/types";

// An @-mentioned RMS record, drawn as a chip (#12154): the record type's icon
// and the name on a pill, in link ink.
//
// Two layouts, one look:
// - "mirror" sits in the composer's highlight mirror behind a textarea, where
//   every character must keep its exact width or the caret drifts off the
//   text. It keeps the "@" (transparent, with the icon painted over it) and
//   gets its padding from a box-shadow spread, which takes no layout space.
//   The icon is anchored to the "@" itself, so a chip that wraps onto a second
//   line keeps its icon on the first.
// - "inline" sits in the sent message, which is free to lay out a real chip,
//   so it drops the "@" altogether and names the kind for screen readers. It
//   is a link: clicking it opens the record in the side panel, by the same
//   `openEntityPreview` event a record citation in an answer sends. The
//   mirror chip cannot be one: it lies under the text being edited, where a
//   click places the caret.

// Link ink (#12154, Gerry's pick of four mockups): the app's link blue on a
// light tint of it. The same in the composer and once sent, so a mention does
// not change colour on send. The mirror needs the tint as a value (its
// padding is a box-shadow); the sent chip needs it as Tailwind classes, which
// must be literal strings: keep `bg-[#F1F3FF]` below in step with this.
const CHIP_BG = "#F1F3FF";

type RecordMentionChipProps =
  | { layout: "mirror"; kind: EntityKind; name: string }
  // The record's public id, which the preview opens by. `truncate` keeps the
  // chip inside a one-line box (a table cell), its name ending in an ellipsis.
  | {
      layout: "inline";
      kind: EntityKind;
      name: string;
      id: string;
      truncate?: boolean;
    };

const RecordMentionChip: React.FC<RecordMentionChipProps> = (props) => {
  const { kind, name } = props;
  if (props.layout === "mirror") {
    return (
      <span
        data-record-mention={kind}
        className="rounded-md text-[#2A3DCB]"
        // The pill's padding is all shadow, which takes no layout space: it
        // reaches into the spaces on either side of the token (about 4px
        // each) and stops short of the neighbouring words, and stays a pixel
        // inside the 24px line so pills on consecutive lines do not touch.
        style={{
          backgroundColor: CHIP_BG,
          boxShadow: `-1.5px 0 0 1px ${CHIP_BG}, 1.5px 0 0 1px ${CHIP_BG}`,
        }}
      >
        <span className="relative">
          <span className="text-transparent">@</span>
          {/* Smaller than the "@" it stands in for and pulled left into the
          pill's padding, which opens a gap between the icon and the name. */}
          <EntityIcon
            kind={kind}
            aria-hidden
            className="absolute left-0 top-1/2 h-[0.7em] w-[0.7em] -translate-y-1/2"
          />
        </span>
        {name}
      </span>
    );
  }
  const { id, truncate = false } = props;
  return (
    <button
      type="button"
      data-record-mention={kind}
      aria-label={`${kind} ${name}`}
      onClick={() =>
        window.dispatchEvent(
          new CustomEvent("openEntityPreview", {
            detail: { entityKind: kind, entityId: id, entityName: name },
          })
        )
      }
      // Underlines on hover, like any other link.
      className={`mx-px inline-flex cursor-pointer items-baseline gap-1 rounded-md bg-[#F1F3FF] px-1.5 text-left font-medium leading-6 text-[#2A3DCB] transition-colors hover:bg-[#E9ECFF] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2A3DCB] ${
        truncate ? "max-w-full align-top" : ""
      }`}
    >
      <EntityIcon
        kind={kind}
        aria-hidden
        className="h-3.5 w-3.5 shrink-0 self-center"
      />
      {truncate ? <span className="min-w-0 truncate">{name}</span> : name}
    </button>
  );
};

export default RecordMentionChip;
